// Parent management credentials are scoped to a purchase, never to an email.
const PREFIX = "wiswiz_parent_capability_v1:";
// Pending keys are retained only so the user can retry persistence. They must
// never authorize an API request until localStorage accepts the write.
const pending = new Map<string, string>();

export class ParentCapabilityStorageError extends Error {
  constructor(public readonly hasPendingCapability: boolean) {
    super("Browser storage is unavailable for the parent capability");
    this.name = "ParentCapabilityStorageError";
  }
}

export function saveParentCapability(token: string, key: string): void {
  if (!token || !key) throw new Error("Parent capability requires a purchase and key");
  pending.set(token, key);
  try {
    window.localStorage.setItem(PREFIX + token, key);
  } catch {
    throw new ParentCapabilityStorageError(true);
  }
  pending.delete(token);
}

export function readParentCapability(token: string): string | null {
  const key = pending.get(token);
  if (key) saveParentCapability(token, key);
  try {
    return window.localStorage.getItem(PREFIX + token);
  } catch {
    throw new ParentCapabilityStorageError(!!key);
  }
}

// instrumentation-client runs before analytics and React. Remove credentials
// before GTM, pixels or replay can read the browser URL. A failed write is
// retried by the page, which renders an explicit error instead of aborting hydration.
export function consumeParentCapabilityUrl(): void {
  const url = new URL(window.location.href);
  const key = url.searchParams.get("parent_key");
  if (!key) return;
  const match = url.pathname.match(/^\/(?:voor-je-kind|voor-ouders\/tweede-kind)\/([^/]+)\/?$/);
  const token = match ? decodeURIComponent(match[1]) :
    url.pathname === "/voor-ouders" ? url.searchParams.get("gift") : null;
  url.searchParams.delete("parent_key");
  window.history.replaceState(window.history.state, "", url.toString());
  if (token) {
    try {
      saveParentCapability(token, key);
    } catch (error) {
      if (!(error instanceof ParentCapabilityStorageError)) throw error;
      // readParentCapability retries and reports this pending write to the UI.
    }
  }
}
